import React, { useEffect, useRef } from 'react';
import './Index.css';
import { Link } from 'react-router-dom';
export default function IndexMain() {
  const cardsRef = useRef([]);
  const themeToggleRef = useRef(null);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.style.animationPlayState = 'running';
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((card) => {
      if (card) {
        card.style.animationPlayState = 'paused';
        observer.observe(card);
      }
    });

    // Theme toggle functionality
    const themeToggle = themeToggleRef.current;
    const htmlElement = document.documentElement;

    function handleThemeToggle() {
      if (htmlElement.getAttribute('data-theme') === 'dark') {
        htmlElement.setAttribute('data-theme', 'light');
        themeToggle.innerHTML = '<i class="fas fa-moon"></i>';
        htmlElement.style.setProperty('--background-color', '#f0f4f8');
        htmlElement.style.setProperty('--card-color', '#ffffff');
        htmlElement.style.setProperty('--text-primary', '#1e293b');
        htmlElement.style.setProperty('--text-secondary', '#64748b');
        htmlElement.style.setProperty('--shadow', '0 4px 20px rgba(0, 0, 0, 0.08)');
      } else {
        htmlElement.setAttribute('data-theme', 'dark');
        themeToggle.innerHTML = '<i class="fas fa-sun"></i>';
        htmlElement.style.setProperty('--background-color', '#121212');
        htmlElement.style.setProperty('--card-color', '#1e1e1e');
        htmlElement.style.setProperty('--text-primary', '#ffffff');
        htmlElement.style.setProperty('--text-secondary', '#b0b0b0');
        htmlElement.style.setProperty('--shadow', '0 4px 20px rgba(0, 0, 0, 0.3)');
      }
    }

    themeToggle.addEventListener('click', handleThemeToggle);

    return () => {
      observer.disconnect();
      themeToggle.removeEventListener('click', handleThemeToggle);
    };
  }, []);

  // Helper to assign refs to cards
  const setCardRef = (el, idx) => {
    cardsRef.current[idx] = el;
  };

  const categories = [
    {
      name: "Technology",
      icon: "fas fa-microchip",
      desc: "Get the latest tech news, innovations, gadget reviews, and digital trends shaping our world.",
      url: "/feed/news.google.com/rss/topics/CAAqKggKIiRDQkFTRlFvSUwyMHZNRGRqTVhZU0JXVnVMVlZUR2dKVlV5Z0FQAQ?hl=en-US&gl=US&ceid=US:en",
      img: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80",
      delay: "0s"
    },
    {
      name: "World News",
      icon: "fas fa-globe",
      desc: "Stay informed about international events, global politics, and major developments around the world.",
      url: "/feed/news.google.com%2Frss%2Ftopics%2FCAAqJggKIiBDQkFTRWdvSUwyMHZNRGx1YlY4U0FtVnVHZ0pWVXlnQVAB%3Fhl%3Den-US%26gl%3DUS%26ceid%3DUS%3Aen",
      img: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80",
      delay: "0.2s"
    },
    {
      name: "Business",
      icon: "fas fa-chart-line",
      desc: "Follow business news, market updates, economic trends, and corporate developments.",
      url: "/feed/news.google.com%2Frss%2Ftopics%2FCAAqJggKIiBDQkFTRWdvSUwyMHZNRGx6TVdZU0FtVnVHZ0pWVXlnQVAB%3Fhl%3Den-US%26gl%3DUS%26ceid%3DUS%3Aen",
      img: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80",
      delay: "0.3s"
    },
    {
      name: "Sports",
      icon: "fas fa-running",
      desc: "Get updates on sports events, matches, player news, and analysis from around the world.",
      url: "/feed/news.google.com%2Frss%2Ftopics%2FCAAqJggKIiBDQkFTRWdvSUwyMHZNRFp1ZEdvU0FtVnVHZ0pWVXlnQVAB%3Fhl%3Den-US%26gl%3DUS%26ceid%3DUS%3Aen",
      img: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&q=80",
      delay: "0.4s"
    },
    {
      name: "Entertainment",
      icon: "fas fa-film",
      desc: "Follow entertainment news, celebrity updates, movie releases, and TV show reviews.",
      url: "/feed/news.google.com%2Frss%2Ftopics%2FCAAqJggKIiBDQkFTRWdvSUwyMHZNREpxYW5RU0FtVnVHZ0pWVXlnQVAB%3Fhl%3Den-US%26gl%3DUS%26ceid%3DUS%3Aen",
      img: "https://images.unsplash.com/photo-1522869635100-9f4c5e86aa37?auto=format&fit=crop&q=80",
      delay: "0.5s"
    },
    {
      name: "Science & Health",
      icon: "fas fa-heartbeat",
      desc: "Stay updated with health news, medical breakthroughs, wellness tips, and scientific developments.",
      url: "/feed/thehindu.com/sci-tech/science/feeder/default.rss",
      img: "https://images.unsplash.com/photo-1532094349884-543bc11b234d?auto=format&fit=crop&q=80",
      delay: "0.6s"
    }
  ];

  return (
    <>
      <div className="theme-toggle" id="themeToggle" ref={themeToggleRef}>
        <i className="fas fa-sun"></i>
      </div>

      <section className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1 className="hero-title">NewsOShort</h1>
          <p className="hero-subtitle">Stay informed with concise summaries of the latest news from your favorite categories</p>
        </div>
      </section>

      <section className="category-section">
        <h2 className="category-title">Choose a News Category</h2>
        <div className="categories-grid">
          {categories.map((cat, idx) => (
            <div
              className="category-card"
              key={cat.name}
              style={{ animationDelay: cat.delay }}
              ref={el => setCardRef(el, idx)}
            >
              <div
                className="category-image"
                style={{ backgroundImage: `url('${cat.img}')` }}
              ></div>
              <div className="category-content">
                <div className="category-icon">
                  <i className={cat.icon}></i>
                </div>
                <h3 className="category-name">{cat.name}</h3>
                <p className="category-description">{cat.desc}</p>
                <Link to={cat.url} className="category-btn">
                  Browse {cat.name} <i className="fas fa-arrow-right"></i>
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
